import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  getMyDashboard,
  getTeamDashboard,
  getUserDashboard,
  getMyQuoteBilling,
  triggerSync,
  type DashboardData,
  type TeamDashboard,
  type TeamMember,
  type SyncSummary,
  type SyncMode,
} from '@/api/commercialDashboardApi'
import type { QuoteBillingRow } from '@/stores/useQuoteBillingDashboardStore'

export type DashboardViewMode = 'me' | 'team' | 'member'
export type DashboardTab = 'portfolio' | 'quote-billing'

export const useCommercialDashboardStore = defineStore('commercialDashboard', () => {
  // ── State ──────────────────────────────────────────────────────────────────

  const loading = ref(false)
  const error = ref<string | null>(null)

  const syncing = ref(false)
  const syncResult = ref<SyncSummary | null>(null)
  const syncError = ref<string | null>(null)

  /** Données du dashboard personnel ou d'un membre de l'équipe */
  const dashboardData = ref<DashboardData | null>(null)

  /** Données de l'équipe (vue manager) */
  const teamData = ref<TeamDashboard | null>(null)

  /** Mode d'affichage actuel */
  const viewMode = ref<DashboardViewMode>('me')

  /** Membre de l'équipe actuellement sélectionné (mode 'member') */
  const selectedMember = ref<TeamMember['user'] | null>(null)

  /** Onglet du tableau clients : 'me' = sociétés dont je suis propriétaire, 'all' = toutes, 'orders' = commandées mais non désigné sur la fiche */
  const clientScope = ref<'me' | 'all' | 'orders'>('me')

  /** Onglet de la page : 'portfolio' = vue société (KPI/renouvellements/clients), 'quote-billing' = mes devis/factures */
  const dashboardTab = ref<DashboardTab>('portfolio')

  /** Devis (+ factures rattachées) dont l'utilisateur connecté est l'auteur — chargés à la demande */
  const myQuoteBillingRows = ref<QuoteBillingRow[]>([])
  const myQuoteBillingLoading = ref(false)
  const myQuoteBillingError = ref<string | null>(null)

  // ── Computed ───────────────────────────────────────────────────────────────

  const isManager = computed(() => (teamData.value?.members?.length ?? 0) > 0)

  const currentData = computed<DashboardData | null>(() => {
    if (viewMode.value === 'team') return null
    return dashboardData.value
  })

  /** Clients filtrés selon l'onglet actif (propriétaire vs tous vs commandes uniquement) */
  const scopedClients = computed(() => {
    const all = currentData.value?.clients ?? []
    if (clientScope.value === 'me') return all.filter((c) => c.isOwner)
    if (clientScope.value === 'orders') return all.filter((c) => !c.isOwner)
    return all
  })

  /** Renouvellements filtrés selon l'onglet actif */
  const scopedRenewals = computed(() => {
    const all = currentData.value?.renewals ?? []
    if (clientScope.value === 'me') return all.filter((r) => r.isOwner)
    if (clientScope.value === 'orders') return all.filter((r) => !r.isOwner)
    return all
  })

  /** KPIs recalculés sur le périmètre de l'onglet actif (cohérent avec le tableau affiché) */
  const scopedKpis = computed(() => {
    const clients = scopedClients.value
    const totalInvoiced = clients.reduce((s, c) => s + (c.totalInvoiced || 0), 0)
    const totalOutstanding = clients.reduce((s, c) => s + (c.outstandingAmount || 0), 0)
    const totalCreditNotes = clients.reduce((s, c) => s + (c.creditNotesAmount || 0), 0)
    return {
      totalClients: clients.length,
      totalInvoiced: Math.round(totalInvoiced * 100) / 100,
      totalOutstanding: Math.round(totalOutstanding * 100) / 100,
      totalCreditNotes: Math.round(totalCreditNotes * 100) / 100,
    }
  })

  /** Nombre de clients par onglet (pour les libellés d'onglets) */
  const scopeCounts = computed(() => {
    const all = currentData.value?.clients ?? []
    return {
      me: all.filter((c) => c.isOwner).length,
      all: all.length,
      orders: all.filter((c) => !c.isOwner).length,
    }
  })

  /** Statistiques commerciales agrégées sur le périmètre de l'onglet actif */
  const scopedStats = computed(() => {
    const clients = scopedClients.value
    const renewals = scopedRenewals.value

    // #2 — valeur du pipeline de renouvellement (contrats expirant < 90j)
    const renewalValue = renewals.reduce((s, r) => s + (r.totalCa || 0), 0)

    // #1 — balance âgée des impayés (somme des tranches)
    const aging = { current: 0, d1_30: 0, d31_60: 0, d61_90: 0, d90plus: 0 }
    for (const c of clients) {
      aging.current += c.aging?.current ?? 0
      aging.d1_30 += c.aging?.d1_30 ?? 0
      aging.d31_60 += c.aging?.d31_60 ?? 0
      aging.d61_90 += c.aging?.d61_90 ?? 0
      aging.d90plus += c.aging?.d90plus ?? 0
    }

    // #5 — marge moyenne (pondérée par le CA signé)
    const marginAmount = clients.reduce((s, c) => s + (c.marginAmount || 0), 0)
    const marginCa = clients.reduce((s, c) => s + (c.marginCa || 0), 0)
    const marginPct = marginCa > 0 ? (marginAmount / marginCa) * 100 : null

    // #6 — CA signé (12 mois) + délai moyen de signature
    const signedCaPeriod = clients.reduce((s, c) => s + (c.signedCaPeriod || 0), 0)
    const signedCount = clients.reduce((s, c) => s + (c.signedCount || 0), 0)
    const signedDelayDaysSum = clients.reduce((s, c) => s + (c.signedDelayDaysSum || 0), 0)
    const avgSignDelay = signedCount > 0 ? Math.round(signedDelayDaysSum / signedCount) : null

    // #7 — taux de transformation devis → commande
    const quotesTotal = clients.reduce((s, c) => s + (c.quotesTotal || 0), 0)
    const conversionRate = quotesTotal > 0 ? (signedCount / quotesTotal) * 100 : null

    return {
      renewalValue,
      aging,
      agingTotal: aging.current + aging.d1_30 + aging.d31_60 + aging.d61_90 + aging.d90plus,
      marginPct,
      signedCaPeriod,
      avgSignDelay,
      conversionRate,
      signedCount,
      quotesTotal,
    }
  })

  const renewalsOverdue = computed(() =>
    scopedRenewals.value.filter((r) => r.renewalStatus === 'overdue')
  )
  const renewalsSoon = computed(() =>
    scopedRenewals.value.filter((r) => r.renewalStatus === 'soon')
  )
  const renewalsUpcoming = computed(() =>
    scopedRenewals.value.filter((r) => r.renewalStatus === 'upcoming')
  )

  // ── Actions ────────────────────────────────────────────────────────────────

  const fetchMyDashboard = async () => {
    loading.value = true
    error.value = null
    try {
      dashboardData.value = await getMyDashboard()
      viewMode.value = 'me'
      selectedMember.value = null
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Erreur lors du chargement'
    } finally {
      loading.value = false
    }
  }

  const fetchTeamDashboard = async () => {
    loading.value = true
    error.value = null
    try {
      teamData.value = await getTeamDashboard()
      viewMode.value = 'team'
      dashboardData.value = null
      selectedMember.value = null
    } catch {
      // Erreur silencieuse — l'utilisateur n'est peut-être pas manager
      teamData.value = null
    } finally {
      loading.value = false
    }
  }

  const fetchMemberDashboard = async (memberUuid: string) => {
    loading.value = true
    error.value = null
    try {
      const result = await getUserDashboard(memberUuid)
      dashboardData.value = result.stats
      selectedMember.value = result.user
      viewMode.value = 'member'
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Erreur lors du chargement'
    } finally {
      loading.value = false
    }
  }

  const fetchMyQuoteBilling = async (params: { search?: string; status?: string } = {}) => {
    myQuoteBillingLoading.value = true
    myQuoteBillingError.value = null
    try {
      const result = await getMyQuoteBilling(params)
      myQuoteBillingRows.value = result.data
    } catch (err) {
      myQuoteBillingError.value = err instanceof Error ? err.message : 'Erreur lors du chargement'
    } finally {
      myQuoteBillingLoading.value = false
    }
  }

  /** Délai d'auto-masquage du bandeau de succès (sans erreur), en ms */
  const SYNC_RESULT_AUTO_HIDE_MS = 3000
  let syncResultTimer: ReturnType<typeof setTimeout> | null = null

  const sync = async (mode: SyncMode = 'quotes') => {
    if (syncResultTimer) {
      clearTimeout(syncResultTimer)
      syncResultTimer = null
    }
    syncing.value = true
    syncResult.value = null
    syncError.value = null
    try {
      const result = await triggerSync(mode)
      syncResult.value = result
      // Masquage auto du bandeau de succès après quelques secondes, sauf s'il y a des erreurs
      if (result.errors.length === 0) {
        syncResultTimer = setTimeout(() => {
          syncResult.value = null
          syncResultTimer = null
        }, SYNC_RESULT_AUTO_HIDE_MS)
      }
      // Recharger les données après la sync
      await init()
    } catch (err) {
      syncError.value = err instanceof Error ? err.message : 'Erreur lors de la synchronisation'
    } finally {
      syncing.value = false
    }
  }

  /**
   * Initialisation : charge le dashboard perso (critique) + l'équipe (optionnel).
   * Un seul loading, erreur équipe silencieuse pour ne pas masquer les données.
   */
  const init = async () => {
    loading.value = true
    error.value = null
    dashboardData.value = null
    teamData.value = null

    const [myResult, teamResult] = await Promise.allSettled([
      getMyDashboard(),
      getTeamDashboard(),
    ])

    // Dashboard perso — critique : on affiche l'erreur si ça échoue
    if (myResult.status === 'fulfilled') {
      dashboardData.value = myResult.value
    } else {
      error.value = myResult.reason instanceof Error
        ? myResult.reason.message
        : 'Erreur lors du chargement du dashboard'
    }

    // Équipe — optionnel : erreur silencieuse (non-manager, réseau...)
    if (teamResult.status === 'fulfilled') {
      teamData.value = teamResult.value
    }

    loading.value = false
    viewMode.value = 'me'
  }

  return {
    // State
    loading,
    error,
    dashboardData,
    teamData,
    viewMode,
    selectedMember,
    clientScope,
    dashboardTab,
    myQuoteBillingRows,
    myQuoteBillingLoading,
    myQuoteBillingError,
    syncing,
    syncResult,
    syncError,
    // Computed
    isManager,
    currentData,
    scopedClients,
    scopedRenewals,
    scopedKpis,
    scopeCounts,
    scopedStats,
    renewalsOverdue,
    renewalsSoon,
    renewalsUpcoming,
    // Actions
    fetchMyDashboard,
    fetchTeamDashboard,
    fetchMemberDashboard,
    fetchMyQuoteBilling,
    sync,
    init,
  }
})
